/**
 * MuxFeedUploader — official Mux Uploader + Direct Uploads API.
 *
 * @see https://www.mux.com/docs/guides/uploader-web-integrate-in-your-webapp
 * @see https://www.mux.com/docs/api-reference/video/direct-uploads/create-direct-upload
 * @see https://www.mux.com/docs/api-reference/video/direct-uploads/list-direct-uploads
 *
 * Meta fija (server):
 *   creator_id  = BRAND_MUX_CREATOR_ID
 *   external_id = "feed"
 *
 * Flujo:
 *   1. MuxUploader endpoint() → POST /api/admin/mux-direct-upload → upload URL
 *   2. UpChunk (inside Mux Uploader) PUT chunks to Mux
 *   3. success → poll GET ?uploadId= until asset ready + playbackId
 *   4. GET list (no uploadId) → tabla de uploads recientes
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MuxUploader from "@mux/mux-uploader-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  BRAND_MUX_CREATOR_ID,
  BRAND_MUX_EXTERNAL_ID_FEED,
} from "@/lib/brandConfig";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  Loader2,
  RefreshCw,
  Upload,
} from "lucide-react";

export type MuxFeedUploadMeta = {
  creator_id: string | null;
  external_id: string | null;
  title: string | null;
};

export type MuxFeedUploaderProps = {
  className?: string;
  onReady?: (playbackId: string, meta: MuxFeedUploadMeta) => void;
  /**
   * Add a ready upload's playback ID to the reels playlist (admin manage).
   * Shown as a button on each ready row in the uploads table.
   */
  onAddToPlaylist?: (playbackId: string) => void;
  /** Playback IDs already in playlist — dims "Agregar" when present. */
  playlistPlaybackIds?: string[];
  disabled?: boolean;
  /**
   * If true, do not list Mux uploads on mount (faster first paint).
   * List loads when user clicks "Actualizar lista" or after a successful upload.
   */
  deferList?: boolean;
};

type UploadListItem = {
  id: string;
  status: string;
  assetId: string | null;
  playbackId: string | null;
  assetStatus: string | null;
  thumbnailUrl: string | null;
  ready: boolean;
  isBrandFeed?: boolean;
  meta: MuxFeedUploadMeta;
  createdAt: number | null;
};

const FEED_EXTERNAL_ID = BRAND_MUX_EXTERNAL_ID_FEED || "feed";
/** ~2 GB in kB for Mux Uploader maxFileSize */
const MAX_FILE_SIZE_KB = 2 * 1024 * 1024;

export function MuxFeedUploader({
  className,
  onReady,
  onAddToPlaylist,
  playlistPlaybackIds,
  disabled,
  deferList = false,
}: MuxFeedUploaderProps) {
  const { getAuthHeaders } = useAdminAuth();
  const titleRef = useRef("");
  const lastUploadIdRef = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const uploaderKeyRef = useRef(0);

  const [title, setTitle] = useState("");
  const [phase, setPhase] = useState<
    "idle" | "uploading" | "processing" | "ready" | "error"
  >("idle");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [playbackId, setPlaybackId] = useState<string | null>(null);
  const [uploadMeta, setUploadMeta] = useState<MuxFeedUploadMeta | null>(null);
  const [uploaderKey, setUploaderKey] = useState(0);

  const [uploads, setUploads] = useState<UploadListItem[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const playlistSet = useMemo(
    () => new Set(playlistPlaybackIds || []),
    [playlistPlaybackIds],
  );

  titleRef.current = title;

  const clearPoll = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  const loadUploads = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const headers = await getAuthHeaders();
      // List direct uploads — official GET /video/v1/uploads
      // brandOnly=1: multi-brand Mux accounts must not leak other brands' uploads
      const res = await fetch(
        "/api/admin/mux-direct-upload?limit=25&page=1&brandOnly=1",
        { headers },
      );
      const data = (await res.json()) as {
        ok?: boolean;
        uploads?: UploadListItem[];
        error?: string;
      };
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `List HTTP ${res.status}`);
      }
      setUploads(Array.isArray(data.uploads) ? data.uploads : []);
    } catch (e) {
      setListError(e instanceof Error ? e.message : "No se pudo listar uploads");
    } finally {
      setListLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!deferList) void loadUploads();
    return () => clearPoll();
  }, [deferList, loadUploads]);

  const pollUntilReady = useCallback(
    async (uploadId: string) => {
      const headers = await getAuthHeaders();
      let attempts = 0;
      const maxAttempts = 120;

      const tick = async () => {
        attempts += 1;
        try {
          const res = await fetch(
            `/api/admin/mux-direct-upload?uploadId=${encodeURIComponent(uploadId)}`,
            { headers },
          );
          const data = (await res.json()) as {
            ok?: boolean;
            ready?: boolean;
            playbackId?: string | null;
            status?: string;
            error?: string;
            meta?: MuxFeedUploadMeta;
          };

          if (data.status === "errored" || (data.ok === false && data.error)) {
            setPhase("error");
            setError(data.error || "Procesamiento falló en Mux");
            void loadUploads();
            return;
          }

          if (data.ready && data.playbackId) {
            setPhase("ready");
            setProgress(100);
            setPlaybackId(data.playbackId);
            const meta = data.meta || {
              creator_id: BRAND_MUX_CREATOR_ID,
              external_id: FEED_EXTERNAL_ID,
              title: titleRef.current || null,
            };
            setUploadMeta(meta);
            onReady?.(data.playbackId, meta);
            void loadUploads();
            return;
          }

          setPhase("processing");
          if (attempts >= maxAttempts) {
            setPhase("error");
            setError(
              "Mux sigue procesando. Revisá la lista de uploads o reintentá más tarde.",
            );
            void loadUploads();
            return;
          }
          pollRef.current = setTimeout(() => void tick(), 2000);
        } catch (e) {
          if (attempts >= maxAttempts) {
            setPhase("error");
            setError(e instanceof Error ? e.message : "Error al consultar estado");
            return;
          }
          pollRef.current = setTimeout(() => void tick(), 3000);
        }
      };

      void tick();
    },
    [getAuthHeaders, loadUploads, onReady],
  );

  /**
   * Official Mux Uploader pattern: endpoint as async function that returns
   * the Direct Upload URL from our authenticated create endpoint.
   */
  const endpoint = useMemo(
    () => async (_file?: File) => {
      if (disabled) throw new Error("Upload deshabilitado");
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/mux-direct-upload", {
        method: "POST",
        headers,
        body: JSON.stringify({
          title: titleRef.current.trim() || undefined,
          externalId: FEED_EXTERNAL_ID,
        }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        uploadUrl?: string;
        url?: string;
        uploadId?: string;
        error?: string;
        meta?: MuxFeedUploadMeta;
      };
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `Create upload HTTP ${res.status}`);
      }
      const uploadUrl = data.uploadUrl || data.url;
      if (!uploadUrl || !data.uploadId) {
        throw new Error("Mux no devolvió upload URL");
      }
      lastUploadIdRef.current = data.uploadId;
      if (data.meta) setUploadMeta(data.meta);
      return uploadUrl;
    },
    [disabled, getAuthHeaders],
  );

  const resetUploader = () => {
    clearPoll();
    lastUploadIdRef.current = null;
    setPhase("idle");
    setProgress(0);
    setError(null);
    setPlaybackId(null);
    uploaderKeyRef.current += 1;
    setUploaderKey(uploaderKeyRef.current);
  };

  return (
    <div
      className={cn(
        "space-y-4 rounded-xl border border-dashed border-white/15 bg-black/20 p-4",
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Upload className="h-4 w-4" /> Subir video (Mux Uploader)
          </h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Direct Upload oficial + UpChunk. Meta fija:{" "}
            <code className="text-[10px]">
              creator_id={BRAND_MUX_CREATOR_ID}
            </code>
            ,{" "}
            <code className="text-[10px]">external_id={FEED_EXTERNAL_ID}</code>.
          </p>
        </div>
        {phase === "ready" && (
          <Badge className="bg-emerald-600/80 text-[10px]">
            <CheckCircle2 className="mr-1 h-3 w-3" /> Listo
          </Badge>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="font-mono text-[10px]">
          creator_id={BRAND_MUX_CREATOR_ID}
        </Badge>
        <Badge variant="outline" className="font-mono text-[10px]">
          external_id={FEED_EXTERNAL_ID}
        </Badge>
      </div>

      <div className="space-y-1.5 max-w-md">
        <Label className="text-xs">Título (opcional, se aplica al crear el upload)</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="reel-marzo / press-kit"
          disabled={disabled || phase === "uploading" || phase === "processing"}
          className="h-9 text-sm"
        />
      </div>

      {/* Official Mux Uploader web component (React wrapper) */}
      <div
        className={cn(
          "mux-feed-uploader rounded-xl border border-white/10 bg-zinc-950/80 p-3",
          disabled && "pointer-events-none opacity-50",
        )}
      >
        <MuxUploader
          key={uploaderKey}
          endpoint={endpoint}
          dynamicChunkSize
          useLargeFileWorkaround
          maxFileSize={MAX_FILE_SIZE_KB}
          pausable
          style={
            {
              "--progress-bar-fill-color": "#a78bfa",
              color: "rgba(255,255,255,0.85)",
              fontFamily: "inherit",
            } as React.CSSProperties
          }
          onUploadStart={() => {
            setError(null);
            setPlaybackId(null);
            setPhase("uploading");
            setProgress(0);
          }}
          onProgress={(e) => {
            const pct = typeof e.detail === "number" ? e.detail : 0;
            setProgress(Math.min(99, Math.round(pct)));
            setPhase("uploading");
          }}
          onSuccess={() => {
            setProgress(100);
            setPhase("processing");
            const id = lastUploadIdRef.current;
            if (id) void pollUntilReady(id);
            else {
              setError("Upload OK pero sin uploadId — revisá la lista abajo");
              void loadUploads();
            }
          }}
          onUploadError={(e) => {
            const msg =
              (e.detail &&
                typeof e.detail === "object" &&
                "message" in e.detail &&
                String((e.detail as { message?: string }).message)) ||
              "Error de upload Mux";
            setPhase("error");
            setError(msg);
            void loadUploads();
          }}
        />
      </div>

      {(phase === "uploading" || phase === "processing") && (
        <p className="text-[10px] text-muted-foreground">
          {phase === "uploading"
            ? `Subiendo chunks a Mux… ${progress}%`
            : "Chunks enviados. Mux está codificando el asset (30s–2min)…"}
        </p>
      )}

      {error && (
        <p className="text-xs text-red-300 rounded-lg border border-red-500/30 bg-red-950/40 px-3 py-2">
          {error}
        </p>
      )}

      {phase === "ready" && playbackId && (
        <div className="flex flex-col gap-2 rounded-lg border border-emerald-500/25 bg-emerald-950/20 p-3 sm:flex-row sm:items-center">
          <img
            src={`https://image.mux.com/${playbackId}/thumbnail.jpg?width=120&height=160&fit_mode=smartcrop&time=1`}
            alt=""
            className="h-20 w-14 rounded object-cover ring-1 ring-white/10"
          />
          <div className="min-w-0 space-y-0.5">
            <p className="text-xs font-medium text-emerald-100">
              Video listo en Mux (feed)
            </p>
            <p className="font-mono text-[10px] text-muted-foreground break-all">
              {playbackId}
            </p>
            <p className="text-[10px] text-muted-foreground">
              meta: creator_id=
              {uploadMeta?.creator_id || BRAND_MUX_CREATOR_ID}, external_id=
              {uploadMeta?.external_id || FEED_EXTERNAL_ID}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="sm:ml-auto"
            onClick={resetUploader}
          >
            Subir otro
          </Button>
        </div>
      )}

      {/* List Direct Uploads */}
      <div className="space-y-2 border-t border-white/[0.06] pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-xs font-semibold text-white/80">
            Uploads recientes (Mux Direct Uploads)
          </h4>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => void loadUploads()}
            disabled={listLoading}
          >
            {listLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
            )}
            Actualizar lista
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground">
          GET /video/v1/uploads vía admin. Vacío = todavía no hay uploads en este
          entorno Mux (el feed /reels puede estar vacío sin problema).
        </p>

        {listError && (
          <p className="text-[11px] text-red-300/90">{listError}</p>
        )}

        <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-white/[0.03] text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 font-medium">Estado</th>
                <th className="px-2 py-1.5 font-medium">Upload / Playback</th>
                <th className="px-2 py-1.5 font-medium">Meta</th>
                <th className="px-2 py-1.5 font-medium">Thumb</th>
                {onAddToPlaylist ? (
                  <th className="px-2 py-1.5 font-medium">Playlist</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {uploads.length === 0 && !listLoading && (
                <tr>
                  <td
                    colSpan={onAddToPlaylist ? 5 : 4}
                    className="px-2 py-4 text-muted-foreground"
                  >
                    Sin direct uploads todavía. Subí el primero arriba.
                  </td>
                </tr>
              )}
              {uploads.map((u) => {
                const inPlaylist =
                  !!u.playbackId && playlistSet.has(u.playbackId);
                return (
                <tr key={u.id} className="border-t border-white/[0.05]">
                  <td className="px-2 py-1.5">
                    <Badge
                      variant={u.ready ? "default" : "outline"}
                      className="text-[9px]"
                    >
                      {u.ready ? "ready" : u.status}
                    </Badge>
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[9px] max-w-[180px]">
                    <div className="truncate" title={u.id}>
                      up:{u.id.slice(0, 12)}…
                    </div>
                    {u.playbackId ? (
                      <div className="truncate text-emerald-200/80" title={u.playbackId}>
                        pb:{u.playbackId.slice(0, 14)}…
                      </div>
                    ) : (
                      <div className="text-muted-foreground">
                        {u.assetId ? `asset:${u.assetId.slice(0, 10)}…` : "—"}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-[9px] text-muted-foreground">
                    <div>
                      {u.meta.creator_id || "—"} / {u.meta.external_id || "—"}
                    </div>
                    {u.meta.title && (
                      <div className="truncate max-w-[120px]">{u.meta.title}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {u.thumbnailUrl ? (
                      <img
                        src={u.thumbnailUrl}
                        alt=""
                        className="h-10 w-7 rounded object-cover"
                      />
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  {onAddToPlaylist ? (
                    <td className="px-2 py-1.5">
                      {u.ready && u.playbackId ? (
                        inPlaylist ? (
                          <Badge variant="outline" className="text-[9px]">
                            En playlist
                          </Badge>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="h-7 text-[10px]"
                            onClick={() => onAddToPlaylist(u.playbackId!)}
                          >
                            + Playlist
                          </Button>
                        )
                      ) : (
                        <span className="text-muted-foreground text-[9px]">—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
